import { useRef, useState } from 'react'
import { ComposableMap, Geographies, Geography, Sphere, Graticule, Marker } from 'react-simple-maps'
import { geoCentroid, geoArea } from 'd3-geo'
import worldData from '../../../../utils/countries-map-units-50m.json'
import { feature } from 'topojson-client'
import { REGIONS, inferCodeFromGeoName, availableCodesFromData, sumCountsByRegionFromData, getRegionByCode } from '../../../../utils/geoRegions'

const BASE_WIDTH = 1400
const BASE_HEIGHT = 650
const BASE_SCALE = 200
const SCALE_PER_PX = BASE_SCALE / BASE_WIDTH

const colors = ['#332288', '#88CCEE', '#44AA99', '#117733', '#DDCC77', '#CC6677', '#AA4499', '#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4']

const buildColorMap = (keys) => keys.reduce((m, k, i) => ((m[k] = colors[i % colors.length]), m), {})

const objKey = Object.keys(worldData.objects)[0]
const countries = feature(worldData, worldData.objects[objKey]).features
const isoFromProps = (p = {}) => (p.iso_a2 || p.ISO_A2_EH || p.ISO_A2 || p.WB_A2 || p.postal || '').slice(0, 2).toUpperCase()

const featuresByIso = new Map()
for (const f of countries) {
  const iso = isoFromProps(f.properties)
  if (iso) featuresByIso.set(iso, f)
}

const ALWAYS_MARK_AS_DOT = new Set(['VI', 'VG', 'SX', 'BQ', 'BM', 'KY', 'JE', 'GG', 'IM', 'AD', 'LI', 'MC', 'SM', 'VA'])
const isTiny = (f) => geoArea(f) < 1e-3

const RegionTotalsPanel = ({ items = [] }) => (
  <div className="country-panel">
    {items.map(({ name, color, total }) => (
      <div className="country-section" key={name}>
        <div className="country-section-title">
          <span className="legend-color-dot" style={{ background: color }} />
          {name}
          <span className="country-section-total"> {Number(total || 0).toLocaleString()}</span>
        </div>
      </div>
    ))}
  </div>
)

export default function GeographicalConnectionMap({ data = [] }) {
  const innerRef = useRef(null)
  const containerRef = useRef(null)
  const scale = BASE_WIDTH * SCALE_PER_PX
  const regions = REGIONS
  const colorMap = buildColorMap(regions)
  const { regionCounts } = sumCountsByRegionFromData(data || [])
  const availableCodes = availableCodesFromData(data || [])
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' })
  const panelItems = [...regions.map((r) => ({ name: r, color: colorMap[r], total: regionCounts[r] || 0 }))]
  const countsByCountry = new Map((data || []).map((r) => [String(r.country || r.countryCode || '').toUpperCase(), Number(r.count || 0)]))
  const dataIsos = availableCodesFromData(data || [])

  const markerCodes = [...dataIsos].filter((iso) => {
    const f = featuresByIso.get(iso)
    if (!f) return true
    return ALWAYS_MARK_AS_DOT.has(iso) || isTiny(f)
  })

  return (
    <div ref={containerRef} className="map-scroll">
      <div className="map-inner" ref={innerRef}>
        <ComposableMap
          width={BASE_WIDTH}
          height={BASE_HEIGHT}
          style={{ width: '100%', height: 'auto', display: 'block' }}
          preserveAspectRatio="xMidYMid meet"
          projectionConfig={{ scale }}
          onMouseMove={(e) => {
            if (!tooltip.visible) return
            setTooltip((t) => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))
          }}
        >
          <defs>
            <radialGradient id="oceanGradient" cx="50%" cy="50%" r="75%">
              <stop offset="0%" stopColor="#eaf4fb" />
              <stop offset="70%" stopColor="#d8ebf8" />
              <stop offset="100%" stopColor="#cbe3f2" />
            </radialGradient>
          </defs>
          <Sphere fill="url(#oceanGradient)" stroke="#bcd4e6" strokeWidth={1} />
          <Graticule stroke="#c9d6e1" strokeWidth={0.5} step={[30, 30]} />
          <Geographies geography={countries}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const props = geo.properties
                const name = props.name
                const iso = isoFromProps(props) || inferCodeFromGeoName(name) || ''
                const region = iso ? getRegionByCode(iso) : null
                const hasData = iso && availableCodes.has(iso)
                const fillColor = hasData && region ? colorMap[region] : '#EEE'
                const count = countsByCountry.get(iso) || 0

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke="#FFF"
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', opacity: 0.85 },
                      pressed: { outline: 'none' },
                    }}
                    onMouseEnter={(e) => {
                      if (hasData && region) {
                        setTooltip({
                          visible: true,
                          x: e.clientX + 12,
                          y: e.clientY + 12,
                          text: `${region}: ${count}`,
                        })
                      }
                    }}
                    onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, text: '' })}
                  />
                )
              })
            }
          </Geographies>
          {markerCodes.map((iso) => {
            const f = featuresByIso.get(iso)
            const [lon, lat] = geoCentroid(f)
            const region = getRegionByCode(iso)
            const fillColor = region ? colorMap[region] : '#666'
            const count = countsByCountry.get(iso) || 0
            return (
              <Marker
                key={`dot-${iso}`}
                coordinates={[lon, lat]}
                onMouseEnter={(e) =>
                  setTooltip({
                    visible: true,
                    x: e.clientX + 12,
                    y: e.clientY + 12,
                    text: `${region}: ${count}`,
                  })
                }
                onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, text: '' })}
              >
                <circle r={3} fill={fillColor} stroke="#fff" strokeWidth={1} />
              </Marker>
            )
          })}
        </ComposableMap>

        {tooltip.visible && (
          <div className="tooltip" style={{ position: 'fixed', pointerEvents: 'none', left: tooltip.x, top: tooltip.y }}>
            {tooltip.text}
          </div>
        )}

        <div className="legend-and-panel">
          <RegionTotalsPanel items={panelItems} />
        </div>
      </div>
    </div>
  )
}

